import { object, string, InferType } from 'yup'
import { useMutation } from '@apollo/client'
import { FC, ReactNode, useCallback } from 'react'
import { useFormik } from 'formik'

import { EMAIL } from 'constants/schema'
import { LoginMutation, LoginMutationVariables, UserNode } from 'gql'
import { useQueryParams, useDataLayer } from 'hooks'
import { USER_BASE_DATA } from 'gql/user/queries'
import { LOGIN_USER } from 'gql/user/mutations'
import { PARTNER_SLUG } from 'constants/constant'
import SignInFormComponent from './component'

const validationSchema = object({
  password: string().required('Password is required!'),
  email: EMAIL(),
}).required()

type SignInFormValues = InferType<typeof validationSchema>

export type SignInFormProps = {
  header?: ReactNode
  onCompleted?: (user: Pick<UserNode, 'isPhoneNumberVerified'>) => void
  initialValues?: Partial<SignInFormValues>
}

const SignInForm: FC<SignInFormProps> = ({ header, onCompleted, initialValues }) => {
  const {
    paths,
    push,
    withQueryParams,
    query: { redirect, auction, bid, reassignCart, follow },
  } = useQueryParams()

  const [mutateLogin] = useMutation<LoginMutation, LoginMutationVariables>(LOGIN_USER, {
    refetchQueries: [USER_BASE_DATA],
  })
  const dl = useDataLayer()

  const onSubmit = useCallback(
    (values, { setStatus }) => {
      mutateLogin({
        variables: { ...values, cartId: reassignCart, partnerSlug: PARTNER_SLUG },
      }).then((res) => {
        if (res.data.login.__typename === 'LoginFailure') {
          setStatus(
            res.data.login.errorMessage || 'An error occurred while logging in. Please try again.'
          )
          return
        }

        dl.push({
          event: 'login',
          method: 'email-pass',
        })

        if (onCompleted) {
          onCompleted({ isPhoneNumberVerified: res.data.login.user.isPhoneNumberVerified })
          return
        }

        if (auction && bid) {
          push({ pathname: paths.CONFIRM_BID, query: { auction, bid } })
          return
        }

        if (follow) {
          push({ pathname: redirect, query: { follow: true } })
          return
        }

        push(redirect || paths.HOME)
      })
    },
    [dl, mutateLogin, paths.CHECKOUT, paths.HOME, push, redirect]
  )

  const { handleSubmit, handleChange, handleBlur, values, status, errors, touched } =
    useFormik<SignInFormValues>({
      initialValues: {
        email: '',
        password: '',
        ...initialValues,
      },
      validationSchema,
      onSubmit,
    })

  return (
    <SignInFormComponent
      header={header}
      values={values}
      status={status}
      errors={errors}
      touched={touched}
      handleBlur={handleBlur}
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      forgotPassword={paths.FORGOT_PASSWORD}
      joinLink={withQueryParams(paths.JOIN, { redirect, auction, bid })}
    />
  )
}

export default SignInForm
